/** Session: the access token lives only in memory; the refresh token is an httpOnly cookie the browser
 * sends to /api/auth/refresh. Refreshes are single-flight within a tab and serialised across tabs. */
import type { Me } from "./types";

export const AUTH_HEADERS = { "X-Requested-With": "reel-studio" };

type Session = { token: string; expiresAt: number; user: Me };
type Listener = (s: Session | null) => void;

let session: Session | null = null;
const listeners = new Set<Listener>();
let inflight: Promise<Session | null> | null = null;
const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("rs.auth") : null;

channel?.addEventListener("message", (e) => {
  if (e.data === "logout") setSession(null, false);
});

export function getSession() {
  return session;
}

export function getToken() {
  return session?.token ?? null;
}

export function onSession(fn: Listener) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setSession(s: Session | null, broadcast = true) {
  session = s;
  listeners.forEach((fn) => fn(s));
  if (!s && broadcast) channel?.postMessage("logout");
}

export function acceptTokenResponse(body: { access_token: string; expires_in: number; user: Me }) {
  setSession({ token: body.access_token, expiresAt: Date.now() + body.expires_in * 1000, user: body.user });
  return session!;
}

async function doRefresh(): Promise<Session | null> {
  const res = await fetch("/api/auth/refresh", { method: "POST", credentials: "same-origin", headers: AUTH_HEADERS });
  if (!res.ok) {
    setSession(null, false);
    return null;
  }
  return acceptTokenResponse(await res.json());
}

/** One refresh at a time (per tab, and across tabs when the browser supports Web Locks). */
export function refresh(): Promise<Session | null> {
  if (!inflight) {
    const run = () => doRefresh();
    const locked =
      typeof navigator !== "undefined" && "locks" in navigator
        ? navigator.locks.request("rs.refresh", run)
        : run();
    inflight = (locked as Promise<Session | null>).finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

/** A valid token, refreshing first if it is about to expire. */
export async function freshToken(): Promise<string | null> {
  if (session && session.expiresAt - Date.now() > 30_000) return session.token;
  return (await refresh())?.token ?? null;
}

export async function logout() {
  try {
    await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin", headers: AUTH_HEADERS });
  } finally {
    setSession(null);
  }
}
